import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Foodstuff } from '../../models/Foodstuff';
import { RecommendedMeal } from '../../models/RecommendedMeal';
import {
  calculateNutritionFromFoodstuffs,
  calculateNutritionFromMeal,
  formatGrams,
  isLocalMealPlaceholder,
  resolveFoodstuffWeightInGrams,
  resolveMealImageUrl
} from '../../common/mealMetrics';
import { uiTokens } from '../../common/uiTokens';

type RecommendedMealCardProps = {
  recommendation: RecommendedMeal;
  variant: 'primary' | 'secondary';
  onLogNow?: (recommendation: RecommendedMeal, compositionFoodstuffs?: Foodstuff[]) => void;
  isLogged?: boolean;
  isLogging?: boolean;
};

const tagLabelKeys: Record<RecommendedMeal['tags'][number], string> = {
  HIGH_PROTEIN: 'recommended_meal.tags.HIGH_PROTEIN',
  LOW_CALORIE: 'recommended_meal.tags.LOW_CALORIE',
  BALANCED_MACROS: 'recommended_meal.tags.BALANCED_MACROS',
  QUICK_PREP: 'recommended_meal.tags.QUICK_PREP',
  COMFORT_FOOD: 'recommended_meal.tags.COMFORT_FOOD',
  LIGHT_MEAL: 'recommended_meal.tags.LIGHT_MEAL',
  HEAVY_MEAL: 'recommended_meal.tags.HEAVY_MEAL',
  VEGETARIAN: 'recommended_meal.tags.VEGETARIAN',
  VEGAN: 'recommended_meal.tags.VEGAN',
};

const typeLabelKeys: Record<RecommendedMeal['type'], string> = {
  PERFECT_DIET: 'recommended_meal.types.PERFECT_DIET',
  YOUR_MEAL: 'recommended_meal.types.YOUR_MEAL',
  SAFE_CHOICE: 'recommended_meal.types.SAFE_CHOICE',
  VARIETY_PICK: 'recommended_meal.types.VARIETY_PICK',
};

const tagColors: Partial<Record<RecommendedMeal['tags'][number], string>> = {
  HIGH_PROTEIN: uiTokens.colors.recommendationBg,
  LOW_CALORIE: uiTokens.colors.adaptiveBg,
  COMFORT_FOOD: uiTokens.colors.info,
};

export function RecommendedMealCard({ recommendation, variant, onLogNow, isLogged = false, isLogging = false }: RecommendedMealCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { meal } = recommendation;
  const [compositionOpen, setCompositionOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(() => resolveMealImageUrl(meal));
  const [compositionFoodstuffs, setCompositionFoodstuffs] = useState<Foodstuff[]>(() =>
    (meal.foodstuffs || []).map((foodstuff) => ({
      ...foodstuff,
      weight: resolveFoodstuffWeightInGrams(foodstuff) || 0,
    }))
  );

  const resolvedImageUrl = resolveMealImageUrl(meal);

  useEffect(() => {
    setImageUrl(resolvedImageUrl);
  }, [resolvedImageUrl]);

  useEffect(() => {
    setCompositionFoodstuffs(
      (meal.foodstuffs || []).map((foodstuff) => ({
        ...foodstuff,
        weight: resolveFoodstuffWeightInGrams(foodstuff) || 0,
      }))
    );
  }, [meal.id, meal.foodstuffs]);

  const isPlaceholderImage = isLocalMealPlaceholder(imageUrl);
  const tags = recommendation.tags.slice(0, 2);
  const reasonLabel = t(typeLabelKeys[recommendation.type]);
  const totals = useMemo(() => calculateNutritionFromMeal(meal), [meal]);
  const editedTotals = useMemo(() => calculateNutritionFromFoodstuffs(compositionFoodstuffs), [compositionFoodstuffs]);

  const fitMeta = useMemo(() => {
    if (totals.fatG > 22 && totals.fatG > totals.proteinG) {
      return {
        tone: 'warning' as const,
        icon: <InfoOutlinedIcon fontSize="small" />,
        message: t('recommended_meal.fit.warning_fat'),
      };
    }

    if (recommendation.type === RecommendedMeal.type.PERFECT_DIET) {
      return {
        tone: 'success' as const,
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        message: t('recommended_meal.fit.great'),
      };
    }

    return {
      tone: 'success' as const,
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      message: t('recommended_meal.fit.adjusted'),
    };
  }, [recommendation.type, t, totals.fatG, totals.proteinG]);

  const fitColors = fitMeta.tone === 'warning'
    ? { backgroundColor: uiTokens.colors.warningBg, color: uiTokens.colors.warningText }
    : {
      backgroundColor: uiTokens.colors.adaptiveBg,
      color: uiTokens.colors.adaptiveText,
      border: `1px solid ${uiTokens.colors.adaptiveBorder}`
    };

  const compositionPreview = compositionFoodstuffs
    .filter((foodstuff) => resolveFoodstuffWeightInGrams(foodstuff))
    .slice(0, 3);

  const handleViewRecipe = () => {
    navigate(`/meal/${meal.id}`, {
      state: {
        adjustedMeal: {
          ...meal,
          foodstuffs: compositionFoodstuffs,
        },
        recommendationType: recommendation.type,
      },
    });
  };

  const handleEditWeight = (index: number, rawValue: string) => {
    const parsed = Number.parseFloat(rawValue);
    const nextWeight = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;

    setCompositionFoodstuffs((prev) => {
      const next = [...prev];
      const item = next[index];
      if (!item) {
        return prev;
      }
      next[index] = {
        ...item,
        weight: nextWeight,
      };
      return next;
    });
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: variant === 'primary' ? 'row' : 'column' },
          backgroundColor: uiTokens.colors.surface,
          border: `1px solid ${uiTokens.colors.border}`,
          boxShadow: variant === 'primary' ? uiTokens.shadow.card : uiTokens.shadow.soft,
          overflow: 'hidden',
          opacity: isLogged ? 0.72 : 1,
          borderRadius: uiTokens.radius.card,
        }}
      >
        <CardMedia
          component="img"
          image={imageUrl}
          alt={meal.name}
          onError={() => setImageUrl('/mealPlaceholder.png')}
          sx={{
            width: { xs: '100%', md: variant === 'primary' ? '40%' : '100%' },
            minHeight: variant === 'primary' ? 240 : 200,
            maxHeight: variant === 'primary' ? { xs: 240, md: 'auto' } : 220,
            objectFit: isPlaceholderImage ? 'contain' : 'cover',
            backgroundColor: isPlaceholderImage ? uiTokens.colors.surfaceMuted : undefined,
            p: isPlaceholderImage ? 2 : 0,
          }}
        />
        <CardContent sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2, p: { xs: 2, md: 2.5 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {meal.mealtime && (
              <Chip
                label={t(`common.mealtime.${meal.mealtime}`)}
                size="small"
                sx={{
                  backgroundColor: uiTokens.colors.surfaceStrong,
                  color: uiTokens.colors.textPrimary,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                }}
              />
            )}
            <Chip
              label={reasonLabel}
              size="small"
              sx={{
                backgroundColor: uiTokens.colors.recommendationBg,
                color: uiTokens.colors.recommendationText,
                fontWeight: 700,
              }}
            />
            {isLogged && (
              <Chip
                label={t('recommended_meal.eaten')}
                size="small"
                sx={{ backgroundColor: uiTokens.colors.adaptiveBg,
                    color: uiTokens.colors.adaptiveText, fontWeight: 700 }}
              />
            )}
          </Box>

          <Box>
            <Typography variant={variant === 'primary' ? 'h5' : 'h6'} sx={{ color: uiTokens.colors.textPrimary, fontWeight: 700 }}>
              {meal.name}
            </Typography>
            <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary, mt: 0.75 }}>
              {t('recommended_meal.adjusted_snapshot')}
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.25 }}>
            <NutritionMetric label={t('recommended_meal.metrics.energy')} value={`${totals.energyKcal.toFixed(0)} kcal`} />
            <NutritionMetric label="P" value={`${totals.proteinG.toFixed(1)} g`} />
            <NutritionMetric label="S" value={`${totals.carbohydrateG.toFixed(1)} g`} />
            <NutritionMetric label="T" value={`${totals.fatG.toFixed(1)} g`} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              borderRadius: 3,
              px: 1.5,
              py: 1.25,
              ...fitColors,
            }}
          >
            <Box sx={{ mt: 0.25 }}>{fitMeta.icon}</Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {fitMeta.message}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                {t('recommended_meal.fit.supporting')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={t(tagLabelKeys[tag])}
                size="small"
                sx={{
                  backgroundColor: tagColors[tag] || uiTokens.colors.surfaceMuted,
                  color: uiTokens.colors.textPrimary,
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>

          {compositionPreview.length > 0 && (
            <Box sx={{ borderTop: `1px solid ${uiTokens.colors.border}`, pt: 1.5 }}>
              <Typography variant="caption" sx={{ color: uiTokens.colors.textMuted, letterSpacing: 0.5 }}>
                {t('recommended_meal.composition_preview')}
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {compositionPreview.map((foodstuff) => (
                  <Box key={foodstuff.id || foodstuff.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: uiTokens.colors.textPrimary }}>
                      {foodstuff.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary, fontWeight: 600 }}>
                      {formatGrams(resolveFoodstuffWeightInGrams(foodstuff), 0)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!isLogged && (
              <Button
                variant="contained"
                onClick={() => onLogNow?.(recommendation, compositionFoodstuffs)}
                startIcon={<CheckCircleOutlineIcon />}
                disabled={isLogging}
                sx={{
                  backgroundColor: uiTokens.colors.primary,
                  color: '#fff',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 999,
                  '&:hover': { backgroundColor: uiTokens.colors.primaryDark },
                }}
              >
                {isLogging ? t('recommended_meal.logging') : t('recommended_meal.log_now')}
              </Button>
            )}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                onClick={() => setCompositionOpen(true)}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  borderColor: uiTokens.colors.border,
                  color: uiTokens.colors.textPrimary,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {t('recommended_meal.adjust_composition')}
              </Button>
              <Button
                variant="text"
                startIcon={<MenuBookOutlinedIcon />}
                onClick={handleViewRecipe}
                sx={{
                  flex: 1,
                  color: uiTokens.colors.recommendationText,
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                {t('meal_card.view_recipe')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={compositionOpen}
        onClose={() => setCompositionOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ color: uiTokens.colors.textPrimary, fontWeight: 700 }}>
            {t('recommended_meal.adjusted_title')}
          </Typography>
          <Typography variant="body2" sx={{ color: uiTokens.colors.textSecondary, mt: 0.5 }}>
            {t('recommended_meal.adjusted_subtitle')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: uiTokens.colors.border }}>
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 3, backgroundColor: uiTokens.colors.surfaceMuted }}>
            <Typography variant="body2" sx={{ color: uiTokens.colors.textPrimary, fontWeight: 600 }}>
              {t('recommended_meal.adjusted_explanation')}
            </Typography>
          </Box>

          <Stack spacing={1.25}>
            {(compositionFoodstuffs || []).map((foodstuff, index) => {
              const weight = foodstuff.weight || 0;
              if (weight <= 0 && !foodstuff.value) {
                return null;
              }

              const kcal = (foodstuff.energyInKcal || 0) * weight;

              return (
                <Box key={foodstuff.id || foodstuff.name}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ color: uiTokens.colors.textPrimary }}>
                      {foodstuff.name}
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={weight}
                      onChange={(event) => handleEditWeight(index, event.target.value)}
                      inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
                      sx={{ width: 130 }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: uiTokens.colors.textSecondary, mt: 0.5 }}>
                    {kcal > 0 ? `${kcal.toFixed(0)} kcal` : ''}
                  </Typography>
                  <Divider sx={{ mt: 1.25, borderColor: uiTokens.colors.border }} />
                </Box>
              );
            })}
          </Stack>

          <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
            <NutritionMetric label={t('recommended_meal.metrics.energy')} value={`${editedTotals.energyKcal.toFixed(0)} kcal`} />
            <NutritionMetric label="P" value={`${editedTotals.proteinG.toFixed(1)} g`} />
            <NutritionMetric label="S" value={`${editedTotals.carbohydrateG.toFixed(1)} g`} />
            <NutritionMetric label="T" value={`${editedTotals.fatG.toFixed(1)} g`} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCompositionOpen(false)} sx={{ color: uiTokens.colors.textSecondary, textTransform: 'none' }}>
            {t('recommended_meal.close')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setCompositionOpen(false);
              handleViewRecipe();
            }}
            sx={{
              backgroundColor: uiTokens.colors.primary,
              textTransform: 'none',
              '&:hover': { backgroundColor: uiTokens.colors.primaryDark },
            }}
          >
            {t('recommended_meal.view_adjusted_recipe')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function NutritionMetric(props: { label: string; value: string }) {
  const { label, value } = props;

  return (
    <Box
      sx={{
        borderRadius: 3,
        backgroundColor: uiTokens.colors.surfaceMuted,
        px: 1.25,
        py: 1.1,
      }}
    >
      <Typography variant="caption" sx={{ color: uiTokens.colors.textMuted, letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: uiTokens.colors.textPrimary, fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}
